
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {StoreClientLoginSuccessResponse} from './StoreClientLoginSuccessResponse';
import {ClientLoginInfoRecord} from '../core/ClientLoginInfoRecord';
import {FieldModelClientLoginInfoRecord} from '../core/ClientLoginInfoRecord';

/**
 * StoreClientLoginSuccessRequest struct
 */
class StoreClientLoginSuccessRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!ClientLoginInfoRecord=} clientLoginInfo
   * @param {!number=} accountActualVersion
   * @constructor
   */
  constructor (parent = new Request(), clientLoginInfo = new ClientLoginInfoRecord(), accountActualVersion = new UInt64(0, 0)) {
    super()
    super.copy(parent)
    this.ClientLoginInfo = clientLoginInfo
    this.AccountActualVersion = accountActualVersion
  }

  /**
   * Copy struct (shallow copy)
   * @this {!StoreClientLoginSuccessRequest}
   * @param {!StoreClientLoginSuccessRequest} other Other struct
   * @returns {!StoreClientLoginSuccessRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.ClientLoginInfo != null) {
      this.ClientLoginInfo = ClientLoginInfoRecord.fromObject(other.ClientLoginInfo)
    } else {
      this.ClientLoginInfo = null
    }
    if (other.AccountActualVersion != null) {
      this.AccountActualVersion = UInt64.fromNumber(other.AccountActualVersion)
    } else {
      this.AccountActualVersion = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!StoreClientLoginSuccessRequest}
   * @returns {!StoreClientLoginSuccessRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new StoreClientLoginSuccessRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new StoreClientLoginSuccessRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!StoreClientLoginSuccessRequest}
   * @param {!StoreClientLoginSuccessRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof StoreClientLoginSuccessRequest)) {
      throw new TypeError('Instance of StoreClientLoginSuccessRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!StoreClientLoginSuccessRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      ClientLoginInfo: ((this.ClientLoginInfo != null) ? this.ClientLoginInfo : null),
      AccountActualVersion: ((this.AccountActualVersion != null) ? this.AccountActualVersion.toNumber() : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return StoreClientLoginSuccessRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!StoreClientLoginSuccessRequest} other Object value
   * @returns {!StoreClientLoginSuccessRequest} Created struct
   */
  static fromObject (other) {
    return new StoreClientLoginSuccessRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!StoreClientLoginSuccessRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return StoreClientLoginSuccessRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!StoreClientLoginSuccessRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4098
  }
}

export { StoreClientLoginSuccessRequest };

/**
 * Fast Binary Encoding StoreClientLoginSuccessRequest field model
 */
class FieldModelStoreClientLoginSuccessRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.ClientLoginInfo = new FieldModelClientLoginInfoRecord(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.AccountActualVersion = new fbe.FieldModelUInt64(buffer, this.ClientLoginInfo.fbeOffset + this.ClientLoginInfo.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.ClientLoginInfo.fbeSize + this.AccountActualVersion.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.ClientLoginInfo.fbeExtra + this.AccountActualVersion.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelStoreClientLoginSuccessRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4098
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ClientLoginInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ClientLoginInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientLoginInfo.fbeSize

    if ((fbeCurrentSize + this.AccountActualVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountActualVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountActualVersion.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {!StoreClientLoginSuccessRequest} fbeValue Default value, defaults is new StoreClientLoginSuccessRequest()
   * @returns {!StoreClientLoginSuccessRequest} StoreClientLoginSuccessRequest value
   */
  get (fbeValue = new StoreClientLoginSuccessRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {!StoreClientLoginSuccessRequest} fbeValue StoreClientLoginSuccessRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ClientLoginInfo.fbeSize) <= fbeStructSize) {
      fbeValue.ClientLoginInfo = this.ClientLoginInfo.get()
    } else {
      fbeValue.ClientLoginInfo = new ClientLoginInfoRecord()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientLoginInfo.fbeSize

    if ((fbeCurrentSize + this.AccountActualVersion.fbeSize) <= fbeStructSize) {
      fbeValue.AccountActualVersion = this.AccountActualVersion.get()
    } else {
      fbeValue.AccountActualVersion = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountActualVersion.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {!StoreClientLoginSuccessRequest} fbeValue StoreClientLoginSuccessRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelStoreClientLoginSuccessRequest}
   * @param {StoreClientLoginSuccessRequest} fbeValue StoreClientLoginSuccessRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.ClientLoginInfo.set(fbeValue.ClientLoginInfo)
    this.AccountActualVersion.set(fbeValue.AccountActualVersion)
  }
}

export { FieldModelStoreClientLoginSuccessRequest };

/**
 * Fast Binary Encoding StoreClientLoginSuccessRequest model
 */
class StoreClientLoginSuccessRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelStoreClientLoginSuccessRequest(this.buffer, 4)
  }

  /**
   * Get the StoreClientLoginSuccessRequest model
   * @this {!StoreClientLoginSuccessRequestModel}
   * @returns {!FieldModelStoreClientLoginSuccessRequest} model StoreClientLoginSuccessRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!StoreClientLoginSuccessRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!StoreClientLoginSuccessRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return StoreClientLoginSuccessRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!StoreClientLoginSuccessRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelStoreClientLoginSuccessRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!StoreClientLoginSuccessRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!StoreClientLoginSuccessRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!StoreClientLoginSuccessRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!StoreClientLoginSuccessRequestModel}
   * @param {!StoreClientLoginSuccessRequest} value StoreClientLoginSuccessRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!StoreClientLoginSuccessRequestModel}
   * @param {!StoreClientLoginSuccessRequest} value StoreClientLoginSuccessRequest value, defaults is new StoreClientLoginSuccessRequest()
   * @return {!object} Deserialized StoreClientLoginSuccessRequest value and its size
   */
  deserialize (value = new StoreClientLoginSuccessRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new StoreClientLoginSuccessRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new StoreClientLoginSuccessRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!StoreClientLoginSuccessRequestModel}
   * @param {!number} prev Previous StoreClientLoginSuccessRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { StoreClientLoginSuccessRequestModel };
StoreClientLoginSuccessRequest.__has_response = true;
StoreClientLoginSuccessRequest.__response_class = StoreClientLoginSuccessResponse;
